import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Table, Button, Label } from '@/ui'
import { DateTime } from 'luxon'

import { BookkeepingStatusLabel } from './BookkeepingStatusLabel'
import {
  fetchAdminUsersBookkeepingReports,
  createBookkeepingReportsFromYear,
} from '@/actions/admin/bookkeepingReportActions'
import { User } from '@/reducers/auth/userReducer'
import { useReselector } from '@/utils/sharedHooks'
import { selectBookkeepingReportsByUserId } from '@/selectors/bookkeepingReportsSelectors'
import GenerateMissingReportsModal from './GenerateMissingReportsModal'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  useAutoReconCUBK,
  isAfterAutoReconPeriod,
} from '../reconciliation/shared'
import { TypographyStyles } from '@/styles'

export const BookkeepingReportsCard = ({ user }: { user: User }) => {
  const dispatch = useAppDispatch()
  const [creatingReports, setCreatingReports] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  useEffect(() => {
    fetchAdminUsersBookkeepingReports(user.id)(dispatch)
  }, [dispatch, user.id])

  const createReports = async (year: string) => {
    setCreatingReports(true)
    await createBookkeepingReportsFromYear({
      userId: user.id,
      year,
    })(dispatch)
    setCreatingReports(false)
    setModalOpen(false)
  }

  const reports = useReselector(selectBookkeepingReportsByUserId, user.id)

  const { isEligible } = useAutoReconCUBK({ userId: user.id })

  return (
    <Card className="w-full">
      <Text variant={TypographyStyles.H2}>Monthly Books</Text>
      <br />
      <Button
        variant="secondary"
        loading={creatingReports}
        disabled={creatingReports}
        onClick={() => setModalOpen(true)}
      >
        Generate Missing Books...
      </Button>
      <br />
      <br />
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={5}>Month</Table.HeaderCell>
            <Table.HeaderCell width={5}>Status</Table.HeaderCell>
            <Table.HeaderCell width={2} />
            {isEligible && <Table.HeaderCell width={4} />}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {reports.map((report, idx) => (
            <Table.Row key={report.id}>
              <Table.Cell>
                <b>
                  {DateTime.fromISO(report.date).toFormat(
                    DATE_FORMATS_LUXON.MONTH_YEAR
                  )}
                </b>
              </Table.Cell>
              <Table.Cell>
                <BookkeepingStatusLabel reportId={report.id.toString()} />
              </Table.Cell>
              <Table.Cell>
                <Link
                  to={`/admin/finances/records/${user.id}/monthly-books/${report.id}`}
                >
                  <Button variant="link">view</Button>
                </Link>
              </Table.Cell>
              {isEligible && !isAfterAutoReconPeriod(report.startDate) && (
                <Table.Cell>
                  {idx === 0 ? (
                    <Label className="text-purple-500"> OB First Month </Label>
                  ) : (
                    <Label className="text-purple-500"> OB </Label>
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {modalOpen && (
        <GenerateMissingReportsModal
          onClose={() => setModalOpen(false)}
          loading={creatingReports}
          onCreateReports={createReports}
        />
      )}
    </Card>
  )
}
